import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Crown, Check, Sparkles, ShieldCheck } from "lucide-react";
import PayPalCheckout from "../components/PayPalCheckout";

// Feature list — Pro is the only paid tier as of 2026-02 (Family collapsed).
const TIER_FEATURES = {
  pro: [
    "2-week balance forecast with paychecks & bills",
    "Multiple bank accounts + transfers",
    "Unlimited receipt scans (Snap Receipt)",
    "Savings Goals + Smart Budgets",
    "SMS text-in (log transactions from any phone)",
    "Bulk-add Templates for recurring lineups",
    "Cancel anytime — no lock-in",
  ],
};

const TIER_ORDER = ["pro"];
const TIER_LABELS = { pro: "Pro" };
const TIER_ICONS = { pro: Crown };

export default function Upgrade() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [busy, setBusy] = useState(null);
  const [interval, setInterval] = useState("yearly"); // "monthly" | "yearly"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/packages");
        setPackages(data.packages || []);
        setTrialDays(data.trial_days || 7);
      } catch (e) {
        toast.error("Could not load pricing");
      }
    })();
  }, []);

  // Group packages by tier + interval for easy lookup
  const byTier = useMemo(() => {
    const out = {};
    packages.forEach((p) => {
      const tier = p.tier || "pro";
      if (!out[tier]) out[tier] = {};
      out[tier][p.interval] = p;
    });
    return out;
  }, [packages]);

  const startCheckout = async (pkg) => {
    if (!pkg) return;
    setBusy(pkg.id);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/payments/checkout/session", {
        package_id: pkg.id,
        origin_url: originUrl,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout unavailable");
        setBusy(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const isPaid = user?.plan === "paid" && !user?.trial_active;
  const onTrial = !!user?.trial_active;
  const trialEnded = !!user?.trial_ended;

  return (
    <div className="space-y-5" data-testid="upgrade-page">
      <div className="flex items-center justify-between">
        <Link to="/" className="nb-btn nb-btn-secondary !py-2 !px-3">
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Pricing</h1>
        <div className="w-10" />
      </div>

      {/* Status banner */}
      {isPaid && (
        <div
          className="nb-card p-4 flex items-center gap-3"
          style={{ background: "#b5e4ca" }}
          data-testid="already-pro-banner"
        >
          <Crown size={22} strokeWidth={3} />
          <div>
            <div className="font-display font-black">
              You&apos;re on Pro
            </div>
            <div className="text-xs font-bold">Enjoying all the good stuff.</div>
          </div>
        </div>
      )}
      {onTrial && (
        <div
          className="nb-card p-4 flex items-center gap-3"
          style={{ background: "#fdecc8" }}
          data-testid="trial-active-banner"
        >
          <Sparkles size={22} strokeWidth={3} />
          <div>
            <div className="font-display font-black">
              {user.trial_days_left ?? 0} day{user.trial_days_left === 1 ? "" : "s"} left in your free trial
            </div>
            <div className="text-xs font-bold">Pick a plan below to keep Pro after {user.trial_ends_at?.slice(0, 10)}.</div>
          </div>
        </div>
      )}
      {trialEnded && (
        <div
          className="nb-card p-4 flex items-center gap-3"
          style={{ background: "#f4c2c2" }}
          data-testid="trial-ended-banner"
        >
          <ShieldCheck size={22} strokeWidth={3} />
          <div>
            <div className="font-display font-black">Your Pro trial has ended</div>
            <div className="text-xs font-bold">Your data is safe — some features are locked until you upgrade.</div>
          </div>
        </div>
      )}

      {/* Billing interval toggle */}
      <div
        className="nb-card p-1 flex gap-1 relative"
        data-testid="interval-toggle"
      >
        <button
          type="button"
          data-testid="interval-monthly"
          onClick={() => setInterval("monthly")}
          className={`flex-1 py-2 rounded-lg font-display font-black text-sm ${
            interval === "monthly" ? "bg-[#1a1a1a] text-[#b5e4ca]" : "bg-transparent"
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          data-testid="interval-yearly"
          onClick={() => setInterval("yearly")}
          className={`flex-1 py-2 rounded-lg font-display font-black text-sm ${
            interval === "yearly" ? "bg-[#1a1a1a] text-[#b5e4ca]" : "bg-transparent"
          }`}
        >
          Yearly
        </button>
        <span
          className="absolute -top-2 -right-2 chip active !bg-[#fdecc8] !text-[9px] !py-0.5 !px-2 shadow-[2px_2px_0_0_#1a1a1a]"
          data-testid="save-tag"
        >
          SAVE 39%
        </span>
      </div>

      {/* Tier cards */}
      <div className="grid gap-3">
        {TIER_ORDER.map((tier) => {
          const pkg = byTier[tier]?.[interval];
          if (!pkg) return null;
          const Icon = TIER_ICONS[tier];
          const isYearly = interval === "yearly";
          const monthEquiv = isYearly ? (pkg.amount / 12).toFixed(2) : null;
          return (
            <div
              key={tier}
              data-testid={`plan-card-${tier}`}
              className="nb-card-lg p-5 space-y-3 relative"
              style={{ background: "#fff" }}
            >
              <div className="flex items-center gap-2">
                <Icon size={20} strokeWidth={3} />
                <div className="font-display font-black text-xl">{TIER_LABELS[tier]}</div>
              </div>
              <div className="flex items-baseline gap-1">
                <div className="font-display font-black text-4xl">${pkg.amount}</div>
                <div className="text-sm font-bold text-[#4a4a4a]">/{isYearly ? "year" : "month"}</div>
              </div>
              {monthEquiv && (
                <div className="text-xs font-bold text-[#4a4a4a]">
                  Just ${monthEquiv}/mo billed yearly
                </div>
              )}
              <ul className="space-y-1.5">
                {(TIER_FEATURES[tier] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm font-medium">
                    <Check size={16} strokeWidth={3} className="mt-0.5 text-[#1a1a1a]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`checkout-${tier}-${interval}`}
                disabled={busy !== null || isPaid}
                onClick={() => startCheckout(pkg)}
                className="nb-btn nb-btn-dark w-full !py-3"
              >
                {busy === pkg.id
                  ? "Redirecting…"
                  : isPaid
                  ? "You're already Pro"
                  : `Get ${TIER_LABELS[tier]} with card`}
              </button>
              {!isPaid && (
                <div className="pt-1">
                  <PayPalCheckout interval={interval} amount={pkg.amount} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center text-xs font-medium text-[#4a4a4a]">
        Every new signup gets a {trialDays}-day free Pro trial · Test mode payments (Stripe &amp; PayPal Sandbox)
      </div>
    </div>
  );
}
